import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

export default function BillingSuccess({ onComplete }) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();
  const hasPolled = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    
    if (!sessionId) {
      setStatus("error");
      return;
    }

    if (hasPolled.current) return;
    hasPolled.current = true;

    // Poll for payment status
    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("error");
        toast.error("Payment verification timed out. Please check your email for confirmation.");
        return;
      }

      try {
        const response = await api.get(`/billing/checkout/status/${sessionId}`);
        const data = response.data;
        setPaymentData(data);

        if (data.payment_status === "paid") {
          // F11: if BE hasn't yet resolved amount (race), poll once more before settling.
          if (data.amount_paid_cents == null && attempts < maxAttempts - 1) {
            setTimeout(() => pollStatus(attempts + 1), 1500);
            return;
          }
          setStatus("success");
          toast.success("Payment successful! Your plan has been upgraded.");
          onComplete?.();
          return;
        } else if (data.status === "expired") {
          setStatus("error");
          toast.error("Payment session expired. Please try again.");
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      }
    };

    pollStatus();
  }, [searchParams, onComplete]);

  return (
    <div className="max-w-md mx-auto mt-20" data-testid="billing-success">
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-indigo-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-indigo-500 mb-2">Processing Payment...</h2>
            <p className="text-indigo-400">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-indigo-500 mb-2">Payment Successful!</h2>
            <p className="text-indigo-400 mb-4">
              Your account has been upgraded to {paymentData?.metadata?.tier || "Pro"}.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Amount: {typeof paymentData?.amount_paid_cents === "number"
                ? `$${(paymentData.amount_paid_cents / 100).toFixed(2)} ${paymentData?.currency?.toUpperCase() || ""}`.trim()
                : "—"}
            </p>
            <button
              onClick={() => navigate("/billing")}
              className="w-full py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors"
            >
              View Billing Details
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-indigo-500 mb-2">Payment Issue</h2>
            <p className="text-indigo-400 mb-6">
              We couldn't verify your payment. Please check your email for confirmation or try again.
            </p>
            <button
              onClick={() => navigate("/billing")}
              className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
            >
              Return to Billing
            </button>
          </>
        )}
      </div>
    </div>
  );
}
